﻿import { App } from './app.js'
import * as Helper from '../helper.js'

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

// Add any template with "partial" in the name to Handlebars.partials
if (!('templates' in Handlebars)) Handlebars.templates = []
Object.entries(Handlebars.templates).forEach(([k, v]) =>
{
	if (k.indexOf('partial') != -1) Handlebars.partials[k.replace('partial/', '')] = v
})

// App/Store Helpers
Handlebars.registerHelper({
	config: (key) => App.Config[key],
	store: (model, id) => (id == undefined || typeof id == 'object') ? App.Store.model(model) : App.Store.model(model)[id],
	storeLoading: (type) => App.Store.loading(type),
	storePageCurrent: (type) => App.Store.paging(type).current?.data?.length ? App.Store.paging(type).current : false,
	storeSetFromRelationships: (relationships, key, id) =>
	{
		if (id && !Array.isArray(id))
			id = [id]

		let set = []

		for (let i in relationships)
		{
			let model = App.Store.model(relationships[i].type, relationships[i].id)
			if (model && id == undefined && (!model.relationships || !model.relationships[key] || !model.relationships[key].data))
			{
				set.push(model)
			}
			else if (model && id && model.relationships && id.indexOf(model.relationships[key]?.data?.id) > -1)
			{
				set.push(model)
			}
		}

		return set
	},
	storeSortKeysByAttribute: (model, attribute) =>
	{
		let models = App.Store.model(model)
		if (!models)
			return []
		let entries = Object.keys(models)
		if (!entries)
			return []
		return entries.sort((a, b) => models[a].attributes[attribute] - models[b].attributes[attribute])
	},
	storeSum: (type, attr) =>
	{
		let sum = 0
		let store = App.Store.model(type)
		for (let id in store)
		{
			if (store[id] && store[id].attributes && store[id].attributes[attr])
			{
				let val = new Number(store[id].attributes[attr])
				if (!isNaN(val))
					sum += val
			}
		}
		return sum
	},
	route(id, data)
	{
		id = id.toLowerCase()
		const route = App.Store.model('route', id);
		if (!route)
		{
			console.warn('Missing route:', id)
			return id
		}

		if (typeof data === 'object' && data.hasOwnProperty('lookupProperty')) // Hbs options parameter
			return route.attributes.url

		let args = Array.from(arguments)
		args.shift()
		return Helper.processStringAsTemplate(route.attributes.url, args)
	},
	currentUser: (attr) => Helper.currentUser(typeof attr == 'string' ? attr : false),
	currentStore: (attr) => Helper.currentStore(typeof attr == 'string' ? attr : false),
	currentConfig: (key) => (typeof key == 'string') ? App.Config[key] : App.Config,
	hasRole: (role) => Helper.hasRole(role),
	hasAdministrativeRole: () => Helper.hasAdministrativeRole(),
	i18n: function () { return Helper.i18n.apply(null, arguments) },
	hbs(template, ...data)
	{
		data.pop()
		if (data.length == 1 && data[0] instanceof App.Model) // Pass the model directly
			data = data[0]
		return Helper.processStringAsTemplate(template, data)
	},
	filterByProperty: (type, prop, val) => Object.values(App.Store.model(type)).filter(obj => obj.attributes[prop] === val || (!val && !obj.attributes.hasOwnProperty(prop))),
	isApexStore: () => !Handlebars.helpers.isPecomStore(),
	isPecomStore: () => App.Store.model('store', App.Config.storeId)?.attributes?.pecom,
	componentProperty: (component, property) => App.Component[component][property]
})

// Content Helpers
Handlebars.registerHelper({
	iconBool: (val, cssClass = '') => new Handlebars.SafeString('<i class="fa fa-' + (Helper.parseBool(val) ? 'check' : 'times') + ' ' + (cssClass ? cssClass : '') + '"></i>'),
	iconTrue: (val, cssClass = '') => Helper.parseBool(val) ? new Handlebars.SafeString('<i class="fa fa-check ' + (cssClass ? cssClass : '') + '"></i>') : '',
	iconFalse: (val, cssClass = '') => Helper.parseBool(val) ? '' : new Handlebars.SafeString('<i class="fa fa-times ' + (cssClass ? cssClass : '') + '"></i>'),
	isObject(item, options)
	{
		return ('object' == (typeof item)) ? options.fn(this) : options.inverse(this)
	},
	dateDiff: (date, date2) =>
	{
		if (date == null || date == undefined || date2 == null || date2 == undefined) return 0
		if (date == 'now') date = undefined;
		if (date2 == 'now') date2 = undefined;
		return moment(date).diff(moment(date2))
	},
	formatDate: (date, format, adjustDays = 0) =>
	{
		if (date == null || date == undefined) return null
		if (date == 'now') date = undefined;
		return moment ? moment(date).add(adjustDays, 'day').format(format) : false
	},
	formatDateUtc: (date, format, adjustDays = 0) =>
	{
		if (date == null || date == undefined) return null
		if (date == 'now') date = undefined;
		return moment ? moment(date).add(adjustDays, 'day').utc().format(format) : false
	},
	formatCurrency: (amount) => isNaN(amount) ? '' : currencyFormatter.format(amount),
	yearRange: (from, to) =>
	{
		let years = []
		const year = new Date().getFullYear()
		for(var i = from; i<to; ++i) years.push(year + i)
		return years
	},
	pagerRange: (num, max) =>
	{
		let to = num + 2
		if (to < 5) to = 5
		if (to > max) to = max
		let from = to - 4
		if (from < 1) from = 1

		return Handlebars.helpers.range(from, to)
	}
})

// Form Helpers
Handlebars.registerHelper({
	optionSelected: (value, test) => { if (value == undefined || test == undefined) return ''; return value == test ? 'selected' : '' },
	inputChecked: (value, test) => { if (value == undefined) return ''; return value == test ? 'checked' : '' },
	inputValue: (value, valueDefault) => (value == undefined) ? valueDefault : value,
	decodeURIComponent: (value) => decodeURIComponent(value)
})

// Utilities
Handlebars.registerHelper({
	eq: (v1, v2) => v1 == v2,
	ne: (v1, v2) => v1 !== v2,
	lt: (v1, v2) => v1 < v2,
	gt: (v1, v2) => v1 > v2,
	lte: (v1, v2) => v1 <= v2,
	gte: (v1, v2) => v1.toFixed(2) >= v2.toFixed(2),
	and()
	{
		return Array.prototype.every.call(arguments, Boolean);
	},
	or()
	{
		return Array.prototype.slice.call(arguments, 0, -1).some(Boolean);
	},
	in: (haystack, needle) =>
	{
		if (typeof haystack === 'string')
		{
			haystack = haystack.toLowerCase()
			needle = needle.toLowerCase()
		}
		if (typeof haystack == 'undefined' || haystack == '')
			return false
		return haystack.indexOf(needle) > -1 ? true : false // Value is in array or is a substring of string 'thing'
	},
	subtract: (v1, v2) => v1 - v2,
	add: (v1, v2) => v1 + v2,
	multiply: (v1, v2) => v1 * v2,
	abs: val => Math.abs(val),
	set: (name, value, options) => // Assign variable value
	{
		if (!options.data.custom) options.data.custom = {}
		options.data.custom[name] = value
	},
	get: (name, options) => name ? options.data.custom[name] : options.data.custom, // Assign variable value
	join: (separator, ...args) => { args.pop(); return args.join(separator).trim() },
	lower: (text) => text?.toLowerCase(),
	split: (separator, value) => value ? value.split(separator) : [],
	range: (from, to) =>
	{
		let range = []
		for (var i = from; i <= to; ++i) range.push(i)
		return range
	},
	newest: (data, count) =>
	{
		let results = []
		let keys = Object.keys(data).sort().reverse().splice(0, count)
		for (let i = 0; i < count; ++i)
			results[keys[i]] = data[keys[i]]
		return results.reverse()
	},
	count: (data) => Array.isArray(data) ? data.length : Object.keys(data).length,
	keys: (obj, reverse) => Helper.parseBool(reverse) ? Object.keys(obj).reverse() : Object.keys(obj),
	objectProperty: (obj, prop) => obj[prop],
	json: (obj) => new Handlebars.SafeString(JSON.stringify(obj)),
	jsonKeyValue: function ()
	{
		let data = {}
		let args = Array.from(arguments)
		args.pop()
		for (let i = 0; i < args.length - 1; i += 2)
			data[args[i]] = args[i + 1]
		return new Handlebars.SafeString(JSON.stringify(data))
	},
	true: (val) => Helper.parseBool(val),
	deleteFromObj: (obj, key) =>
	{
		delete obj[key]
		return obj
	}
});

//#region Quick Order
Handlebars.registerHelper({
	defaultReorderScheduleDays: () =>
	{
		let scheduleDays = 0
		const id = Object.keys(App.Store.model('cartitem')).reverse()[0]
		if (id)
			scheduleDays = App.Store.model('cartitem', id)?.attributes?.scheduleDays
		return scheduleDays || 0
	},
	optionsReorderScheduleDays()
	{
		if (!this._reorderScheduleDaysOptions)
		{
			let mappedOptions = {}
			let optionValues = Helper.currentStore('reorderScheduleDaysOptions')
			for (let i in optionValues)
			{
				if (optionValues[i])
					mappedOptions[optionValues[i]] = Helper.i18n('apex.ui.cart.scheduledays', optionValues[i])
				else
					mappedOptions[optionValues[i]] = Helper.i18n('apex.ui.cart.scheduledays.onetimeorder')
			}

			this._reorderScheduleDaysOptions = mappedOptions
		}

		return this._reorderScheduleDaysOptions
	},
	optionsQuantity(product)
	{
		let options = {}
		let promotion = false

		if (product.relationships?.promotion)
			promotion = App.Store.model('promotion', product.relationships.promotion.data.id)

		for (let i = 1; i < 12; ++i)
		{
			options[i] = i.toString()
			if (promotion && promotion.attributes.quantityBuy == i)
			{
				options[i] += ' + ' + promotion.attributes.quantityGet + ' '
				if (promotion.attributes.discountPercentage == 100)
					options[i] += Helper.i18n('apex.ui.cart.free')
				else
					options[i] += Helper.i18n('apex.ui.cart.percentoff', promotion.attributes.discountPercentage)
			}
		}

		return options
	},
	optionsShipping(shippingOptions)
	{
		let options = {}
		shippingOptions.sort((a, b) => (a?.attributes?.rate ?? 0) - (b?.attributes?.rate ?? 0))
		for (let option of shippingOptions)
		{
			let group = option?.attributes?.group
			if (group)
			{
				if (!options[group])
					options[group] = []

				options[group].push(option)
			}
		}

		return options
	},
	checkoutToOrder: (checkout) => Helper.checkoutToOrder(checkout)
})
//#endregion Quick Order